<script setup>
import { Icon } from '@iconify/vue';
import { matchNavPath } from '@/utils/navActive';

const props = defineProps({
  item: Object,
  level: Number,
  favAction: { type: String, default: null },
});

const emit = defineEmits(['add', 'remove']);
const { t } = useI18n();
const route = useRoute();

const title = computed(() =>
  props.item?.titleKey ? t(props.item.titleKey) : props.item?.title || ''
);

const isHere = computed(() =>
  matchNavPath(props.item?.to, route.path, props.item?.exact === true)
);

function onFavClick(e) {
  e.preventDefault();
  e.stopPropagation();
  if (props.favAction === 'add' && props.item?.to) emit('add', props.item.to);
  if (props.favAction === 'remove' && props.item?.to) emit('remove', props.item.to);
}

function badgeTone(color) {
  const c = String(color || '').toLowerCase();
  if (c === 'warning' || c === 'orange' || c === 'amber') return 'warn';
  if (c === 'success' || c === 'teal' || c === 'green') return 'ok';
  if (c === 'info' || c === 'primary' || c === 'indigo' || c === 'blue') return 'info';
  return 'hot';
}
</script>

<template>
  <v-list-item
    :href="item.external ? item.to : undefined"
    :to="!item.external ? item.to : undefined"
    :active="isHere"
    active-class="nav-is-active"
    class="nav-link mb-1"
    :class="{ 'nav-is-active': isHere }"
    :disabled="item.disabled"
    :target="item.external === true ? '_blank' : undefined"
  >
    <template #prepend>
      <span class="nav-link__icon" :class="'text-' + (item.BgColor || '')">
        <Icon :icon="'solar:' + item.icon" height="18" width="18" class="dot" />
      </span>
    </template>
    <v-list-item-title class="nav-link__title">{{ title }}</v-list-item-title>
    <v-list-item-subtitle v-if="item.subCaption" class="text-caption mt-n1 hide-menu">
      {{ item.subCaption }}
    </v-list-item-subtitle>
    <template #append>
      <div class="nav-link__append">
        <span v-if="isHere" class="nav-here-pip" aria-hidden="true" />
        <span
          v-if="item.chip && favAction !== 'remove'"
          class="nav-badge"
          :class="'nav-badge--' + badgeTone(item.chipColor)"
          :title="item.chipTitle || t('nav.pendingCount', { n: item.chip })"
        >{{ item.chip }}</span>
        <button
          v-if="favAction === 'add'"
          type="button"
          class="nav-pin-ico nav-pin-ico--add"
          :title="t('fav.pin')"
          @click="onFavClick"
        >
          <v-icon size="16">mdi-pin-outline</v-icon>
        </button>
        <button
          v-else-if="favAction === 'remove'"
          type="button"
          class="nav-pin-ico nav-pin-ico--remove"
          :title="t('fav.unpin')"
          @click="onFavClick"
        >
          <v-icon size="16">mdi-close</v-icon>
        </button>
      </div>
    </template>
  </v-list-item>
</template>

<style scoped>
.nav-link__append {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  min-height: 28px;
  flex-shrink: 0;
  max-width: 42%;
}
.nav-link__title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.nav-here-pip {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.35);
  flex-shrink: 0;
}
.nav-pin-ico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid transparent;
  border-radius: 7px;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  line-height: 0;
  -webkit-tap-highlight-color: transparent;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease,
    transform 0.12s ease;
}
.nav-pin-ico--add {
  color: #64748b;
  background: #f1f5f9;
  border-color: #e2e8f0;
}
.nav-pin-ico--add:hover,
.nav-pin-ico--add:active {
  color: #1d4ed8;
  background: #dbeafe;
  border-color: #93c5fd;
  transform: scale(1.05);
}
.nav-pin-ico--remove {
  color: #be123c;
  background: #fff1f2;
  border-color: #fecdd3;
}
.nav-pin-ico--remove:hover,
.nav-pin-ico--remove:active {
  color: #fff;
  background: #e11d48;
  border-color: #be123c;
}
</style>
